import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import BackButton from '../../../../components/ui-components/BackButton'
import { getTopPadding } from '../../../../utils/sessionStorage'
import { goBack, lightImpactVibrate, onReady } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingScreenLoaded,
  trackTaxHarvestingButtonTap,
  trackTaxHarvestingUserClicked
} from '../../tracking/taxHarvestingTracking'
import TaxHarvestingFooter from '../../components/summary/TaxHarvestingFooter'
import LtcgBottomSheet from '../../bottom-sheets/LtcgBottomSheet'
import StcgBottomSheet from '../../bottom-sheets/StcgBottomSheet'
import UnlockTaxPlanBottomSheet from '../../bottom-sheets/UnlockTaxPlanBottomSheet'
import FinancialYearSelectionBottomSheet from '../../bottom-sheets/FinancialYearSelectionBottomSheet'
import { SELECT_FINANCIAL_YEAR_HEADING_ID } from '../../bottom-sheets/financialYearSelectionConstants'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import {
  fetchTaxHarvestingSummary,
  getSelectedTaxHarvestingFinancialYear,
  setSelectedTaxHarvestingFinancialYear
} from '../../api/taxHarvestingSummary'
import type { TaxHarvestingSummaryResponse } from '../../types/summary.types'
import { GainType, HarvestingPlanType } from '../../types/summary.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { formatFyDisplay } from '../../utils/misc'
import { PageTitle } from '../../components/PageTitle'
import { WithdrawalsResponse } from '../../types/withdrawals.types'
import { fetchWithdrawals } from '../../api/withdrawalsApi'
import ThreeLineLoading from '../../../../components/ui-components/loading/ThreeLineLoading'
import { TaxSummaryCard } from '../../components/summary/TaxSummaryCard'
import { TaxSummaryLockedCard } from '../../components/summary/TaxSummaryLockedCard'
import { TaxPlanCard } from '../../components/summary/TaxPlanCard'
import { TaxPlanComingSoonCard } from '../../components/summary/TaxPlanComingSoonCard'
import {
  getLtcgHighlightSubtitle,
  getLtcgHighlightTitle,
  getStcgHighlightSubtitle,
  getStcgHighlightTitle
} from '../../components/summary/summaryHighlightUtils'
import { TaxLiabilityHeader } from '../../components/summary/TaxLiabilityHeader'
import RenderHTML from '../../../../components/ui-components/RenderHTML'

function getFinancialYearFromHeaderLabel(financialYearLabel: string): number {
  const match = financialYearLabel.match(/^FY (\d{4})-\d{2}$/)
  if (!match) {
    return new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1
  }
  return Number(match[1])
}

function getFinancialYearHeaderLabel(financialYear: number): string {
  return `FY ${financialYear}-${String(financialYear + 1).slice(-2)}`
}

export default function TaxHarvestingSummaryPage() {
  const navigate = useNavigate()
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getSelectedTaxHarvestingFinancialYear())
  const { data, isLoading, isError, isSuccess } = useQuery<TaxHarvestingSummaryResponse>({
    queryKey: ['taxHarvestingSummary', selectedFinancialYear],
    queryFn: () => fetchTaxHarvestingSummary(selectedFinancialYear)
  })

  useQuery<WithdrawalsResponse>({
    queryKey: ['taxHarvestingWithdrawals', selectedFinancialYear],
    queryFn: () => fetchWithdrawals(selectedFinancialYear)
  })

  useEffect(() => {
    if (isSuccess || isError) {
      onReady()
    }
  }, [isSuccess, isError])

  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  useEffect(() => {
    trackTaxHarvestingScreenLoaded(TaxHarvestingScreenName.Summary)
  }, [])

  const fyHeaderLabel = useMemo(
    () =>
      formatFyDisplay({
        format: 'short',
        currentFyStart: data?.currentFyStart,
        currentFyEnd: data?.currentFyEnd,
        currentFy: data?.currentFy
      }),
    [data?.currentFyStart, data?.currentFyEnd, data?.currentFy]
  )
  const comingSoonFyHeaderLabel = 'FY 2026-27'
  const availableFyHeaderLabel = useMemo(() => {
    const match = comingSoonFyHeaderLabel.match(/^FY (\d{4})-(\d{2})$/)
    if (!match) {
      return fyHeaderLabel
    }
    const startYear = Number(match[1]) - 1
    const endYearSuffix = String(startYear + 1).slice(-2)
    return `FY ${startYear}-${endYearSuffix}`
  }, [comingSoonFyHeaderLabel, fyHeaderLabel])
  const financialYears = useMemo(
    () => [...new Set([availableFyHeaderLabel, comingSoonFyHeaderLabel].filter(Boolean))],
    [availableFyHeaderLabel, comingSoonFyHeaderLabel]
  )
  const visibleFyHeaderLabel = getFinancialYearHeaderLabel(selectedFinancialYear) || fyHeaderLabel
  const isCurrentFinancialYear = selectedFinancialYear === getFinancialYearFromHeaderLabel(comingSoonFyHeaderLabel)

  const handleBack = () => {
    trackTaxHarvestingButtonTap({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.Back,
      buttonLabel: TaxHarvestingButtonLabel.Back
    })
    lightImpactVibrate()
    goBack()
  }

  const handleViewWithdrawals = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.ViewWithdrawals
    })
    lightImpactVibrate()
    navigate('/tax-harvesting/withdrawals')
  }

  const fyHeaderButtonRef = useRef<HTMLButtonElement>(null)

  const closeFySelectionSheet = () => {
    closeBottomSheet()
    // Restore screen reader focus to the trigger once the sheet has closed (iOS audit Tax summary_B11)
    window.setTimeout(() => fyHeaderButtonRef.current?.focus(), 400)
  }

  const handleFyHeaderClick = () => {
    if (!fyHeaderLabel) {
      return
    }
    openBottomSheet(
      <FinancialYearSelectionBottomSheet
        selectedFinancialYear={getFinancialYearHeaderLabel(selectedFinancialYear)}
        financialYears={financialYears}
        onConfirm={(financialYearLabel) => {
          const nextFinancialYear = getFinancialYearFromHeaderLabel(financialYearLabel)
          setSelectedTaxHarvestingFinancialYear(nextFinancialYear)
          setSelectedFinancialYear(nextFinancialYear)
        }}
        onDismiss={closeFySelectionSheet}
      />,
      closeFySelectionSheet,
      { drawerTitleId: SELECT_FINANCIAL_YEAR_HEADING_ID }
    )
  }

  const handleLtcgPlanClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.LtcgPlanCard
    })
    lightImpactVibrate()

    const targetPlanType = ltcgPlans[0]?.folioTaxHarvestingType ?? HarvestingPlanType.EQ_LTCG
    navigate(`/tax-harvesting/plan?harvestType=${targetPlanType}&gainType=${GainType.LTCG}`)
  }

  const handleLockedPlanClick = (eventId: string) => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId
    })
    lightImpactVibrate()
    openBottomSheet(
      <UnlockTaxPlanBottomSheet
        totalTaxSave={totalPossibleTaxSave}
        validityLabel={`31st March 2026`}
        onDismiss={closeBottomSheet}
      />,
      closeBottomSheet
    )
  }

  const handleLtcgPlanClickWithEliteCheck = () => {
    // if (!isUserElite) {
    //   handleLockedPlanClick(TaxHarvestingEventId.LtcgPlanCard)
    //   return
    // }
    handleLtcgPlanClick()
  }

  const handleStcgPlanClickWithEliteCheck = () => {
    // if (!isUserElite) {
    //   handleLockedPlanClick(TaxHarvestingEventId.StcgPlanCard)
    //   return
    // }
    handleStcgPlanClick()
  }

  const handleStcgPlanClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.StcgPlanCard
    })
    lightImpactVibrate()

    const targetPlanType = stcgPlans[0]?.folioTaxHarvestingType ?? HarvestingPlanType.EQ_STCG
    navigate(`/tax-harvesting/plan?harvestType=${targetPlanType}&gainType=${GainType.STCG}`)
  }

  const handleLtcgPlanClickNonEquity = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.LtcgPlanCard
    })
    lightImpactVibrate()
    navigate(`/tax-harvesting/plan?harvestType=${HarvestingPlanType.NEQ_LTCG}&gainType=${GainType.LTCG}`)
  }

  const handleLtcgPlanClickNonEquityWithEliteCheck = () => {
    // if (!isUserElite) {
    //   handleLockedPlanClick(TaxHarvestingEventId.LtcgPlanCard)
    //   return
    // }
    handleLtcgPlanClickNonEquity()
  }

  const handleStcgPlanClickNonEquity = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.StcgPlanCard
    })
    lightImpactVibrate()
    navigate(`/tax-harvesting/plan?harvestType=${HarvestingPlanType.NEQ_STCG}&gainType=${GainType.STCG}`)
  }

  const handleStcgPlanClickNonEquityWithEliteCheck = () => {
    // if (!isUserElite) {
    //   handleLockedPlanClick(TaxHarvestingEventId.StcgPlanCard)
    //   return
    // }
    handleStcgPlanClickNonEquity()
  }

  const handleLtcgSummaryClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.LtcgSummary
    })
    openBottomSheet(
      <LtcgBottomSheet view='summary' summary={data} longTermGainsSummary={data?.longTermGainsSummary} onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  const handleLtcgTaxClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.LtcgTaxLiability
    })
    openBottomSheet(
      <LtcgBottomSheet view='tax' summary={data} longTermGainsSummary={data?.longTermGainsSummary} onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  const handleStcgSummaryClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.StcgSummary
    })
    openBottomSheet(
      <StcgBottomSheet view='summary' summary={data} shortTermGainsSummary={data?.shortTermGainsSummary} onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  const handleStcgTaxClick = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.Summary,
      eventId: TaxHarvestingEventId.StcgTaxLiability
    })
    openBottomSheet(
      <StcgBottomSheet view='tax' summary={data} shortTermGainsSummary={data?.shortTermGainsSummary} onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`

  const netCapitalGains = data?.netCapitalGains ?? 0
  const totalTaxLiability = data?.totalTaxLiability ?? 0
  const netRealisedLTCG = data?.netRealisedLtcg ?? 0
  const netRealisedSTCG = data?.netRealisedStcg ?? 0
  const taxLiabilityLTCG = data?.taxLiabilityLtcg ?? 0
  const taxLiabilitySTCG = data?.taxLiabilityStcg ?? 0
  const ltcgPlans = data?.ltcgFinancialPlanResponses ?? []
  const equityLtcgPlan = ltcgPlans.find(
    (plan) => plan.folioTaxHarvestingType === 'EQ_LTCG' || plan.folioTaxHarvestingType === 'EQ_LTCL'
  )
  const nonEquityLtcgPlan = ltcgPlans.find(
    (plan) => plan.folioTaxHarvestingType === 'NEQ_LTCG' || plan.folioTaxHarvestingType === 'NEQ_LTCL'
  )
  const hasEquityLtcgPlan = !!equityLtcgPlan
  const hasNonEquityLtcgPlan = !!nonEquityLtcgPlan
  const hasLtcgPlan = hasEquityLtcgPlan || hasNonEquityLtcgPlan
  const stcgPlans = data?.stcgFinancialPlanResponses ?? []
  const equityStcgPlan = stcgPlans.find(
    (plan) => plan.folioTaxHarvestingType === 'EQ_STCG' || plan.folioTaxHarvestingType === 'EQ_STCL'
  )
  const nonEquityStcgPlan = stcgPlans.find(
    (plan) => plan.folioTaxHarvestingType === 'NEQ_STCG' || plan.folioTaxHarvestingType === 'NEQ_STCL'
  )
  const hasEquityStcgPlan = !!equityStcgPlan
  const hasNonEquityStcgPlan = !!nonEquityStcgPlan
  const hasStcgPlan = hasEquityStcgPlan || hasNonEquityStcgPlan
  const isUserElite = data?.isUserElite ?? false
  const totalPossibleTaxSave =
    (ltcgPlans.reduce((sum, p) => sum + (p?.possibleTaxSave ?? 0), 0) ?? 0) +
    (stcgPlans.reduce((sum, p) => sum + (p?.possibleTaxSave ?? 0), 0) ?? 0)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const taxLiabilityHeaderRef = useRef<HTMLDivElement>(null)
  const [isTaxLiabilityHeaderInView, setIsTaxLiabilityHeaderInView] = useState(true)

  useEffect(() => {
    const scrollRoot = scrollContainerRef.current
    const cardElement = taxLiabilityHeaderRef.current
    if (!scrollRoot || !cardElement) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTaxLiabilityHeaderInView(entry?.isIntersecting ?? false)
      },
      { root: scrollRoot, threshold: 0 }
    )

    observer.observe(cardElement)
    return () => observer.disconnect()
  }, [isSuccess])

  if (isLoading) {
    return (
      <ThreeLineLoading />
    )
  }

  if (isError) {
    return (
      <div className='h-screen bg-white flex flex-col items-center justify-center px-5'>
        <p className='text-[14px] font-[400] text-[#6A6E6E]'>
          Something went wrong while loading your tax summary. Please try again.
        </p>
      </div>
    )
  }

  return (
    <div className='h-screen w-full flex flex-col overflow-hidden'>
      <div className={`relative z-[0] pb-4 ${isTaxLiabilityHeaderInView ? 'bg-[#FFF2DF]' : ''}`}>
        <div style={{ top: `calc(${getTopPadding()}px + 8px)`, left: '19px' }} className='flex absolute'>
          <BackButton size={24} onClick={handleBack} />
        </div>
        {visibleFyHeaderLabel ? (
          <button
            type='button'
            ref={fyHeaderButtonRef}
            onClick={handleFyHeaderClick}
            aria-haspopup='dialog'
            aria-label={visibleFyHeaderLabel}
            aria-description='Double tap to open financial year selection'
            style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
            className='w-full h-max flex items-center justify-center text-[#232323] font-[400] text-[14px] cursor-pointer bg-transparent border-none outline-none focus:outline-none focus-visible:outline-none'
          >
            {visibleFyHeaderLabel}
            <span className='ml-1' aria-hidden='true'>
              <RenderHTML
                innerHTMLText={`<svg xmlns="http://www.w3.org/2000/svg" width="5" height="12" viewBox="0 0 5 12" fill="none">
<g clip-path="url(#clip0_74626_16706)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M2.85355 1.66476C2.65829 1.44508 2.34171 1.44508 2.14645 1.66476L0.146463 3.91476C0.00346387 4.07563 -0.0393159 4.31756 0.0380737 4.52776C0.115463 4.73795 0.297782 4.875 0.500011 4.875H4.49999C4.70222 4.875 4.88454 4.73795 4.96193 4.52776C5.03932 4.31756 4.99654 4.07563 4.85354 3.91476L2.85355 1.66476ZM0.0380737 7.47224C0.115463 7.26205 0.297782 7.125 0.500011 7.125H4.49999C4.70222 7.125 4.88454 7.26205 4.96193 7.47224C5.03932 7.68243 4.99654 7.92437 4.85354 8.08524L2.85355 10.3353C2.65829 10.5549 2.34171 10.5549 2.14645 10.3353L0.146463 8.08524C0.00346387 7.92437 -0.0393159 7.68243 0.0380737 7.47224Z" fill="#232323"/>
</g>
<defs>
<clipPath id="clip0_74626_16706">
<rect width="5" height="12" fill="white"/>
</clipPath>
</defs>
</svg>`}
              />
            </span>
          </button>
        ) : null}
      </div>
      <div
        ref={scrollContainerRef}
        role='main'
        className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar overscroll-none'
      >
        {/*
            Uncomment below before taking tax to production
        */}
        <div ref={taxLiabilityHeaderRef}>
          <TaxLiabilityHeader totalTaxLiability={totalTaxLiability} isUserElite={isUserElite} isCurrentFinancialYear={isCurrentFinancialYear} />
        </div>
        <div className='px-5 pb-6'>
          {/* {(ltcgPlans?.length > 0 || stcgPlans?.length > 0) && (
            <div className="pt-2 pb-6">
              <TaxSaveUnlockCard totalTaxSave={totalPossibleTaxSave} isUserElite={isUserElite} />
            </div>
          )} */}
          <PageTitle title='Tax Summary' subtitle='For your mutual fund portfolio' />
          {isUserElite ? (
            <>
              <TaxSummaryCard
                netCapitalGains={format(netCapitalGains)}
                totalTaxLiability={format(totalTaxLiability)}
                onViewWithdrawals={handleViewWithdrawals}
              />
              <h2 className='text-sm font-medium text-[#232323] mt-4'>Long term summary</h2>
              <TaxPlanCard
                label={`Net realised ${GainType.LTCG}`}
                amount={format(netRealisedLTCG)}
                taxLiability={format(taxLiabilityLTCG)}
                highlightTitle={getLtcgHighlightTitle({
                  hasEquityLtcgPlan,
                  hasNonEquityLtcgPlan,
                  equityLtcgPlan,
                  nonEquityLtcgPlan
                })}
                highlightSubtitle={getLtcgHighlightSubtitle(
                  hasEquityLtcgPlan,
                  equityLtcgPlan?.taxFreeBookAmount ?? equityLtcgPlan?.gainBookAmount ?? nonEquityLtcgPlan?.gainBookAmount ?? 0,
                  equityLtcgPlan
                )}
                secondaryHighlightTitle={
                  hasEquityLtcgPlan && hasNonEquityLtcgPlan
                    ? `Save <span style='font-weight: 600;'> ${roundOffAndFormatNumberWithCurrency(nonEquityLtcgPlan?.possibleTaxSave ?? 0)} </span> in tax`
                    : undefined
                }
                secondaryHighlightSubtitle={getStcgHighlightSubtitle(nonEquityLtcgPlan?.gainBookAmount ?? 0)}
                onClick={handleLtcgPlanClickWithEliteCheck}
                onSecondaryHighlightClick={handleLtcgPlanClickNonEquityWithEliteCheck}
                onSummaryClick={handleLtcgSummaryClick}
                onTaxLiabilityClick={handleLtcgTaxClick}
                showNoOpportunity={!hasLtcgPlan}
                gainType={GainType.LTCG}
              />
              <h2 className='text-sm font-medium text-[#232323] mt-4'>Short term summary</h2>
              <TaxPlanCard
                label={`Net realised ${GainType.STCG}`}
                amount={`${netRealisedSTCG < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(netRealisedSTCG)}`}
                taxLiability={`${roundOffAndFormatNumberWithCurrency(taxLiabilitySTCG)}`}
                highlightTitle={getStcgHighlightTitle({
                  hasEquityStcgPlan,
                  hasNonEquityStcgPlan,
                  equityStcgPlan,
                  nonEquityStcgPlan
                })}
                highlightSubtitle={getStcgHighlightSubtitle(
                  (hasEquityStcgPlan ? equityStcgPlan?.gainBookAmount : nonEquityStcgPlan?.gainBookAmount) ?? 0
                )}
                secondaryHighlightTitle={
                  hasEquityStcgPlan && hasNonEquityStcgPlan
                    ? `Save <span style='font-weight: 600;'> ${roundOffAndFormatNumberWithCurrency(nonEquityStcgPlan?.possibleTaxSave ?? 0)} </span> in tax`
                    : undefined
                }
                secondaryHighlightSubtitle={
                  hasEquityStcgPlan && hasNonEquityStcgPlan ? 'See your non-equity tax saving plan' : undefined
                }
                onClick={handleStcgPlanClickWithEliteCheck}
                onSecondaryHighlightClick={handleStcgPlanClickNonEquityWithEliteCheck}
                onSummaryClick={handleStcgSummaryClick}
                onTaxLiabilityClick={handleStcgTaxClick}
                showNoOpportunity={!hasStcgPlan}
                gainType={GainType.STCG}
              />
              {isCurrentFinancialYear ? (
                <TaxPlanComingSoonCard financialYearLabel={comingSoonFyHeaderLabel} />
              ) : null}
            </>
          ) : (
            <div className='relative mt-6 overflow-hidden rounded-[16px] border border-[#EBF2F2] bg-white'>
              <TaxSummaryCard
                embedded
                netCapitalGains='₹****'
                totalTaxLiability='₹****'
              />
              <div
                className='pointer-events-none absolute inset-0 backdrop-blur-[2px]'
                style={{
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 18%, rgba(255,255,255,0.35) 32%, rgba(255,255,255,0.82) 46%, #ffffff 58%)'
                }}
              />
              <div className='relative z-10 px-6 pb-8'>
                <TaxSummaryLockedCard />
              </div>
            </div>
          )}
        </div>
        <TaxHarvestingFooter compactForNonEliteSummary={!isUserElite} />
      </div>
    </div>
  )
}
